/**
 * 存储模块统一导出
 */

import type { IStorage, StorageOptions } from './Storage'
import { BrowserStorage } from './BrowserStorage'
import { ServerStorage } from './ServerStorage'
import { MemoryStorage } from './MemoryStorage'
import { UniAppStorage } from './UniAppStorage'

export * from './Storage'
export { BrowserStorage, ServerStorage, MemoryStorage, UniAppStorage }

/**
 * 检测当前环境
 *
 * 判断优先级：browser > uniapp > server
 *
 * 关键约束：
 * 1. 只要有 window（含 WebView 内嵌 H5），一律走 BrowserStorage，
 *    因为 uni-app H5/WebView 环境下 localStorage 是可用的。
 * 2. uniapp 仅在完全没有 window 的小程序原生环境下才选中，
 *    且必须验证 uni.getStorageSync 真实可调用，防止编译注入的空引用误判。
 */
export function detectEnvironment(): 'browser' | 'uniapp' | 'server' {
  // 有 window 就是浏览器/H5/WebView 环境，直接用 BrowserStorage
  if (typeof window !== 'undefined') {
    return 'browser'
  }

  // 无 window 时才考虑 uniapp 原生小程序环境
  const uniObj =
    (typeof uni !== 'undefined' ? uni : null) ??
    (typeof globalThis !== 'undefined' ? (globalThis as any).uni : null)
  if (uniObj && typeof uniObj.getStorageSync === 'function') {
    return 'uniapp'
  }

  return 'server'
}

/**
 * 创建存储实例（自动检测环境）
 */
export function createStorage(options?: StorageOptions): IStorage {
  const env = detectEnvironment()

  if (env === 'browser') {
    return new BrowserStorage(options)
  }

  if (env === 'uniapp') {
    return new UniAppStorage(options?.prefix)
  }

  return new ServerStorage(options)
}

/**
 * 创建内存存储实例（用于测试）
 */
export function createMemoryStorage(options?: StorageOptions): IStorage {
  return new MemoryStorage(options)
}
